import React, { useEffect, useRef, useState } from "react";
import Tippy from "@tippyjs/react";
import { roundArrow } from "tippy.js";
import "tippy.js/dist/tippy.css";
import "tippy.js/dist/svg-arrow.css";
import { Button } from "./Button";

export type TutorialTooltipPosition = 
  | "top" 
  | "top-start" 
  | "top-end" 
  | "bottom" 
  | "bottom-start" 
  | "bottom-end" 
  | "left" 
  | "left-start" 
  | "left-end" 
  | "right" 
  | "right-start" 
  | "right-end";

export interface TutorialTooltipStep {
  /** Unique identifier for this step */
  id: string;
  /** The selector for the element to anchor to (e.g., "#begin-button", ".praxis-score-card"). Use "body" for modal/centered display */
  anchorSelector: string;
  /** Position of the tooltip relative to the anchor */
  position?: TutorialTooltipPosition;
  /** Title text for the tooltip */
  title: string;
  /** Description text for the tooltip */
  description: string;
  /** Text for the action button (default: "Next") */
  actionButtonText?: string;
  /** Text for the skip button (default: "Skip for now") */
  skipButtonText?: string;
  /** Whether this is the last step */
  isLastStep?: boolean;
  /** Whether to display as a modal (centered) instead of a tooltip */
  isModal?: boolean;
  /** Route path where this step should be shown (e.g., "/dashboard", "/praxisplay/role-play"). Omit or use showWhenElementOnPage to show whenever the anchor is on the page. */
  route?: string;
  /** When true, step is shown whenever the anchor element is on the page and visible (route is not checked). Use for steps that can appear on multiple routes. */
  showWhenElementOnPage?: boolean;
  /** Whether this step requires an active session (for steps 4-6) */
  requiresActiveSession?: boolean;
  /** Optional callback to execute when the action button is clicked (before moving to next step) */
  onAction?: () => void | Promise<void>;
  /** Optional callback to execute when the skip button is clicked */
  onSkip?: () => void;
}

interface TutorialTooltipProps {
  /** Current step to display */
  step: TutorialTooltipStep | null;
  /** Whether the tutorial is visible (false when skipped) */
  isVisible: boolean;
  /** Callback when action button is clicked */
  onAction: () => void;
  /** Callback when skip button is clicked */
  onSkip: () => void;
  /** Whether to show the full-screen overlay (default false) */
  showOverlay?: boolean;
}

/** True if the element has non-zero size and is within the viewport (actually visible to the user). */
function isElementVisible(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  const margin = 1;
  return (
    rect.top < window.innerHeight + margin &&
    rect.left < window.innerWidth + margin &&
    rect.bottom > -margin &&
    rect.right > -margin
  );
}

export function TutorialTooltip({
  step,
  isVisible,
  onAction,
  onSkip,
  showOverlay = false,
}: TutorialTooltipProps) {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [overlayStyle, setOverlayStyle] = useState<React.CSSProperties>({});
  const [isTransitioning, setIsTransitioning] = useState(false);

  const updateOverlay = React.useCallback((element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    const padding = 8;
    setOverlayStyle({
      clipPath: `polygon(
        0% 0%, 0% 100%,
        ${rect.left - padding}px 100%, ${rect.left - padding}px ${rect.top - padding}px,
        ${rect.right + padding}px ${rect.top - padding}px, ${rect.right + padding}px ${rect.bottom + padding}px,
        ${rect.left - padding}px ${rect.bottom + padding}px, ${rect.left - padding}px 100%,
        100% 100%, 100% 0%
      )`,
    });
  }, []);

  useEffect(() => {
    if (!step || !isVisible) {
      setAnchorElement(null);
      setIsTransitioning(false);
      return;
    }

    // For modal mode, use body as anchor
    if (step.isModal) {
      setAnchorElement(document.body);
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let intervalId: ReturnType<typeof setInterval> | null = null;
    let retryCount = 0;
    const maxRetries = 100; // Try for up to 10 seconds (100 * 100ms)

    // Find the anchor element; only set anchor when element exists and is visible
    const findElement = () => {
      const element = document.querySelector(step.anchorSelector) as HTMLElement;
      if (element && isElementVisible(element)) {
        setAnchorElement(prevElement => {
          if (prevElement !== element && showOverlay) updateOverlay(element);
          return prevElement !== element ? element : prevElement;
        });
        return true;
      }
      setAnchorElement(null);
      return false;
    };

    // Try synchronously first so step transitions feel instant when anchor is already in DOM and visible
    const foundSync = findElement();

    const retry = () => {
      if (findElement()) return;
      retryCount++;
      if (retryCount < maxRetries) {
        timeoutId = setTimeout(retry, 100);
      }
    };
    if (!foundSync) {
      timeoutId = setTimeout(retry, 50);
    }

    // Always poll so we pick up anchor changes (e.g. selected HCP card) and visibility changes
    intervalId = setInterval(findElement, 400);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [step, isVisible, showOverlay, updateOverlay]);

  useEffect(() => {
    if (!showOverlay || !anchorElement) return;
    const handleResize = () => updateOverlay(anchorElement);
    const handleScroll = () => updateOverlay(anchorElement);
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);
    const interval = setInterval(() => updateOverlay(anchorElement), 300);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
      clearInterval(interval);
    };
  }, [showOverlay, anchorElement, updateOverlay]);

  // Hide immediately when advancing to next step to avoid flicker
  if (isTransitioning) {
    return null;
  }

  // For modal mode, we can render even without a specific anchor (uses body)
  if (!step || !isVisible) {
    return null;
  }

  // For non-modal mode, we need an anchor element
  if (!step.isModal && !anchorElement) {
    return null;
  }

  // Only show tooltip when anchor matches current step (avoids new content on old anchor for a frame)
  if (!step.isModal && anchorElement && document.querySelector(step.anchorSelector) !== anchorElement) {
    return null;
  }

  // For steps requiring active session, check if session is active
  // This is checked at the component level (PraxisCall component)

  const handleAction = async () => {
    // Hide tooltip immediately to avoid flicker, then show next step when ready
    setIsTransitioning(true);

    // Call custom onAction if provided first
    if (step.onAction) {
      await step.onAction();
    } else {
      // If no custom onAction, check if we should trigger the anchor element's click
      if (anchorElement && !step.isLastStep) {
        const isClickable =
          anchorElement.tagName === "BUTTON" ||
          anchorElement.tagName === "A" ||
          anchorElement.getAttribute("role") === "button" ||
          anchorElement.onclick !== null ||
          anchorElement.getAttribute("onclick") !== null;

        if (isClickable) {
          anchorElement.click();
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    }

    onAction();
    // Show next step's tooltip after a short delay so anchor can be found without flicker
    setTimeout(() => setIsTransitioning(false), 200);
  };

  const handleSkip = () => {
    if (step.onSkip) {
      step.onSkip();
    }
    onSkip();
  };

  // Modal mode - positioned bottom-right (matching Figma)
  if (step.isModal) {
    return (
      <>
        {showOverlay && (
          <div className="fixed inset-0 bg-black/50 z-[9998] pointer-events-none" />
        )}
        <div className="fixed inset-0 z-[9999] flex items-end justify-end pointer-events-none p-6">
          <div className="tutorial-tooltip-modal bg-white rounded-lg p-6 min-w-[300px] max-w-[500px] pointer-events-auto">
            <div className="mb-4">
              <h3 className="text-xl font-semibold text-text-primary mb-2">
                {step.title}
              </h3>
              <p className="text-sm text-text2">
                {step.description}
              </p>
            </div>
            <div className="flex justify-end items-center gap-3">
              <button
                type="button"
                onClick={handleSkip}
                className="text-sm text-text2 hover:text-text-primary transition-colors"
              >
                {step.skipButtonText || "Skip for now"}
              </button>
              <Button
                variant="primary"
                onClick={handleAction}
                className="!px-6 !py-2 !text-sm !rounded-full"
              >
                {step.actionButtonText || (step.isLastStep ? "Complete" : "Next")}
              </Button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {showOverlay && (
        <div
          className="fixed inset-0 bg-black/50 z-[9998] pointer-events-none"
          style={overlayStyle}
        />
      )}
      <Tippy
        content={
          <div className="p-4 min-w-[300px] max-w-[400px]">
            <div className="mb-3">
              <h3 className="text-lg font-semibold text-text-primary mb-1">
                {step.title}
              </h3>
              <p className="text-sm text-text2">
                {step.description}
              </p>
            </div>
            <div className="flex justify-between items-center gap-2">
              <button
                type="button"
                onClick={handleSkip}
                className="text-sm text-text2 hover:text-text-primary transition-colors"
              >
                {step.skipButtonText || "Skip for now"}
              </button>
              <Button
                variant="primary"
                onClick={handleAction}
                className="!px-4 !py-2 !text-sm !rounded-full"
              >
                {step.actionButtonText || (step.isLastStep ? "Complete" : "Next")}
              </Button>
            </div>
          </div>
        }
        visible={true}
        reference={anchorElement}
        placement={step.position || "bottom"}
        interactive={true}
        interactiveBorder={10}
        appendTo={document.body}
        zIndex={9999}
        offset={[0, 10]}
        arrow={roundArrow}
        className="tutorial-tooltip"
      />
    </>
  );
}
